/**
 * useStockValidation
 *
 * Real-time stock validation hook.
 * On "Proceed to Checkout", fetches live variant-size quantities fresh from
 * the server and compares them against what the user has in Redux cart.
 *
 * ✅ Self-contained – reads cart_id directly from Redux (state.cart.cart_id)
 * ✅ No extra props needed beyond cartItems
 * ✅ Does NOT touch any existing cart / discount / checkout logic
 *
 * Usage:
 *   const { validateStock, isValidating, stockIssues, clearIssues } =
 *     useStockValidation({ cartItems });
 *
 *   // On checkout button click:
 *   const issues = await validateStock();
 *   if (issues.length > 0) { setShowStockModal(true); return; }
 *   router.push('/checkout');
 */

import { useState, useCallback } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import { useSelector } from 'react-redux';

// ─── GQL ─────────────────────────────────────────────────────────────────────
// Fetches only what we need for stock validation.
// Live qty for a size   → cart_item_sizes → product_variant_size.quantity
// Live qty for no-size  → product.product_variations.quantity
const VALIDATE_STOCK_QUERY = gql`
  query ValidateCartStock($cartId: uuid!) {
    order_carts(where: { id: { _eq: $cartId } }) {
      id
      cart_items(
        where: {
          _and: [
            { deleted_at: { _is_null: true } }
            { is_deleted: { _eq: false } }
          ]
        }
      ) {
        id
        product_id
        product_variation_id
        quantity
        cart_item_sizes {
          id
          variant_size_id
          plating_id
          quantity
          product_variant_size {
            id
            quantity
          }
        }
        product {
          name
          product_variations {
            id
            quantity
          }
        }
      }
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────
/**
 * @typedef {Object} StockIssue
 * @property {'out_of_stock' | 'exceeds_stock'} type
 * @property {string}       productName
 * @property {number}       product_id
 * @property {number|null}  product_variation_id
 * @property {number|null}  variant_size_id
 * @property {number|null}  plating_id
 * @property {string|null}  ring_size        – US size label
 * @property {string|null}  ring_inr_size    – IN size label
 * @property {string|null}  plating_name
 * @property {number}       cartQty          – what user has in cart
 * @property {number}       availableQty     – what's actually in stock right now
 */

// ─── Live stock map builder ───────────────────────────────────────────────────
/**
 * From the GQL response, build two fast-lookup Maps:
 *   bySizeId      → Map<variant_size_id, liveQty>
 *   byVariationId → Map<product_variation_id, liveQty>  (no-size products)
 *
 * Source of truth: product_variant_size.quantity (direct inventory field)
 */
function buildLiveStockMaps(orderCarts) {
  /** @type {Map<number, number>} */
  const bySizeId = new Map();
  /** @type {Map<number, number>} */
  const byVariationId = new Map();

  for (const cart of orderCarts) {
    for (const cartItem of cart.cart_items ?? []) {
      const sizes = cartItem.cart_item_sizes ?? [];

      if (sizes.length > 0) {
        // ── Size-based product ────────────────────────────────────────────
        for (const sizeEntry of sizes) {
          const vsId = sizeEntry.variant_size_id;
          if (vsId == null || bySizeId.has(vsId)) continue;

          // product_variant_size.quantity is the live DB inventory count
          const liveQty = sizeEntry.product_variant_size?.quantity ?? 0;
          bySizeId.set(vsId, liveQty);
        }
      } else {
        // ── No-size product ───────────────────────────────────────────────
        const varId = cartItem.product_variation_id;
        if (varId == null || byVariationId.has(varId)) continue;

        const variation = (cartItem.product?.product_variations ?? []).find(
          (v) => v.id === varId
        );
        byVariationId.set(varId, variation?.quantity ?? 0);
      }
    }
  }

  return { bySizeId, byVariationId };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
/**
 * @param {Object} params
 * @param {Array}  params.cartItems  – from useCart() (same array CartPage renders)
 */
export function useStockValidation({ cartItems }) {
  console.log("stock",cartItems)
  const client = useApolloClient();

  // Reads cart_id from Redux — set by setCartId() action in cartSlice
  const cartId = useSelector((state) => state.cart.cart_id);

  const [isValidating, setIsValidating] = useState(false);
  const [stockIssues, setStockIssues]   = useState(/** @type {StockIssue[]} */ ([]));

  const validateStock = useCallback(async () => {
    if (!cartItems?.length) return [];

    if (!cartId) {
      console.warn('[useStockValidation] cart_id not in Redux (state.cart.cart_id) — skipping validation');
      return [];
    }
console.log('Redux Cart Items:', cartItems);
    setIsValidating(true);
    setStockIssues([]);

    try {
      const { data, error } = await client.query({
        query: VALIDATE_STOCK_QUERY,
        variables: { cartId },
        fetchPolicy: 'network-only', // always bypass Apollo cache — need fresh stock
      });

      console.log("valid",data)
      if (error) {
        // Network/GQL error: fail open so we never block the user from checking out
        console.error('[useStockValidation] GQL error:', error);
        return [];
      }

      const orderCarts = data?.order_carts ?? [];
      if (!orderCarts.length) {
        console.warn('[useStockValidation] No order_cart found for id:', cartId);
        return [];
      }

      const { bySizeId, byVariationId } = buildLiveStockMaps(orderCarts);

      /** @type {StockIssue[]} */
      const issues = [];

      for (const item of cartItems) {
         const isWholesale = (item.sub_category_name ?? '').toLowerCase().includes('wholesale');
        if (isWholesale) continue;
        const hasWholesaleSelections =
          Array.isArray(item.wholesaleSelections) && item.wholesaleSelections.length > 0;

        if (hasWholesaleSelections) {
          // ── Multi-size / wholesale product ────────────────────────────────
          for (const sel of item.wholesaleSelections) {
            const liveQty = bySizeId.get(sel.variantSizeId) ?? null;
            if (liveQty === null) continue; // size not in server response — skip safely

            if (liveQty === 0) {
              issues.push({
                type:                 'out_of_stock',
                productName:          item.name,
                product_id:           item.product_id,
                product_variation_id: item.product_variation_id ?? null,
                variant_size_id:      sel.variantSizeId,
                plating_id:           sel.plating_id ?? null,
                ring_size:            sel.ring_size ?? null,
                ring_inr_size:        sel.ring_inr_size ?? null,
                plating_name:         sel.plating_name ?? null,
                cartQty:              sel.qty,
                availableQty:         0,
              });
            } else if (sel.qty > liveQty) {
              issues.push({
                type:                 'exceeds_stock',
                productName:          item.name,
                product_id:           item.product_id,
                product_variation_id: item.product_variation_id ?? null,
                variant_size_id:      sel.variantSizeId,
                plating_id:           sel.plating_id ?? null,
                ring_size:            sel.ring_size ?? null,
                ring_inr_size:        sel.ring_inr_size ?? null,
                plating_name:         sel.plating_name ?? null,
                cartQty:              sel.qty,
                availableQty:         liveQty,
              });
            }
          }
        } else {
          // ── Single-size or no-size product ───────────────────────────────
          let liveQty = null;

          if (item.variant_size_id != null) {
            liveQty = bySizeId.get(item.variant_size_id) ?? null;
          } else if (item.product_variation_id != null) {
            liveQty = byVariationId.get(item.product_variation_id) ?? null;
          }

          if (liveQty === null) continue;

          const cartQty = item.quantity ?? 1;

          if (liveQty === 0) {
            issues.push({
              type:                 'out_of_stock',
              productName:          item.name,
              product_id:           item.product_id,
              product_variation_id: item.product_variation_id ?? null,
              variant_size_id:      item.variant_size_id ?? null,
              plating_id:           item.plating_id ?? null,
              ring_size:            item.ring_size ?? null,
              ring_inr_size:        item.ring_inr_size ?? null,
              plating_name:         item.plating_name ?? null,
              cartQty,
              availableQty: 0,
            });
          } else if (cartQty > liveQty) {
            issues.push({
              type:                 'exceeds_stock',
              productName:          item.name,
              product_id:           item.product_id,
              product_variation_id: item.product_variation_id ?? null,
              variant_size_id:      item.variant_size_id ?? null,
              plating_id:           item.plating_id ?? null,
              ring_size:            item.ring_size ?? null,
              ring_inr_size:        item.ring_inr_size ?? null,
              plating_name:         item.plating_name ?? null,
              cartQty,
              availableQty: liveQty,
            });
          }
        }
      }

      setStockIssues(issues);
      return issues;

    } catch (err) {
      console.error('[useStockValidation] unexpected error:', err);
      return []; // fail open — never block checkout on our own error
    } finally {
      setIsValidating(false);
    }
  }, [cartItems, cartId, client]);

  const clearIssues = useCallback(() => setStockIssues([]), []);

  return {
    /** Call on "Proceed to Checkout" – returns StockIssue[] */
    validateStock,
    /** true while network request is in-flight */
    isValidating,
    /** Issues from last validateStock() call */
    stockIssues,
    /** Reset issues state (call on modal close) */
    clearIssues,
  };
}